import { Button } from 'antd';

import {
  EDITABLE_IME_STATUS,
  EE_STATUS,
  IME_STATUS,
  STATUS,
} from '~/core/constants';
import i18n from '~/core/i18n/i18n';
import type { EstimateStatus } from '~/core/models/save-estimate-dto';
import { GetEstimateDto } from '~/core/models/save-estimate-dto';
import {
  NotifierType,
  PaymentAvailable,
  SimpleMessage,
  StatusError,
  showNotification,
} from '~/legacy/components';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type NotifyFromStatusParams = {
  currentStatus: EstimateStatus;
  estimate: GetEstimateDto;
  onCopyEstimate?: () => void;
  onNewEstimate?: () => void;
};

// ============================================================================
// STATUS PREDICATES
// ============================================================================

/**
 * Checks if estimate is in a terminal state (closed/expired).
 */
export const isTerminalStatus = (status?: string | null): boolean =>
  status === STATUS.CLOSED || status === STATUS.EXPIRED;

/**
 * Gets the translation section key for terminal status.
 */
const getTerminalStatusTranslationKey = (
  status: STATUS.CLOSED | STATUS.EXPIRED
): string => {
  const sectionTransl: Record<STATUS.CLOSED | STATUS.EXPIRED, string> = {
    [STATUS.CLOSED]: 'CLOSED',
    [STATUS.EXPIRED]: 'EXPIRED',
  };

  return `NOTIFICATIONS.FETCH_ESTIMATE.${sectionTransl[status]}`;
};

// ============================================================================
// STATUS CHANGE MESSAGE BUILDER
// ============================================================================

/**
 * Builds the change message for status updates based on from/to values.
 */
const buildStatusChangeMessage = (
  from: string | null,
  to: string | null
): string => {
  const baseKey = 'NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED';

  if (from && to) {
    return i18n.t(`${baseKey}.FROM_TO`, { from, to });
  }
  if (to) {
    return i18n.t(`${baseKey}.TO`, { from, to });
  }
  if (from) {
    return i18n.t(`${baseKey}.FROM`, { from, to });
  }

  return '';
};

// ============================================================================
// ACTION BUTTONS BUILDER
// ============================================================================

/**
 * Builds action buttons JSX for expired/closed notification.
 */
const buildActionButtons = (
  baseTransl: string,
  onCopyEstimate?: () => void,
  onNewEstimate?: () => void
): React.ReactNode => {
  if (!onCopyEstimate && !onNewEstimate) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        marginTop: '16px',
        flexWrap: 'wrap',
      }}
    >
      {onCopyEstimate && (
        <Button type="primary" onClick={onCopyEstimate}>
          {i18n.t(`${baseTransl}.COPY_BUTTON`) as string}
        </Button>
      )}
      {onNewEstimate && (
        <Button onClick={onNewEstimate}>
          {i18n.t(`${baseTransl}.NEW_BUTTON`) as string}
        </Button>
      )}
    </div>
  );
};

// ============================================================================
// NOTIFICATION HANDLERS
// ============================================================================

/**
 * Shows notification for closed/expired estimate with action buttons.
 */
const showTerminalStatusNotification = (
  currentStatus: EstimateStatus,
  estimate: GetEstimateDto,
  onCopyEstimate?: () => void,
  onNewEstimate?: () => void
): void => {
  const baseTransl = getTerminalStatusTranslationKey(
    currentStatus.status as STATUS.CLOSED | STATUS.EXPIRED
  );
  const notificationKey = `expired-${estimate?.estimateNumber}-${currentStatus.status}`;
  const actionButtons = buildActionButtons(
    baseTransl,
    onCopyEstimate,
    onNewEstimate
  );

  showNotification(
    NotifierType.ERROR,
    i18n.t(`${baseTransl}.TITLE`),
    <StatusError
      description={i18n.t(`${baseTransl}.DESC`)}
      message={i18n.t(`${baseTransl}.MSG`)}
    >
      {actionButtons}
    </StatusError>,
    '',
    0,
    notificationKey
  );
};

/**
 * Shows payment available notification with 60 second duration.
 */
const showPaymentNotification = (
  currentStatus: EstimateStatus,
  estimate: GetEstimateDto
): void => {
  showNotification(
    NotifierType.SUCCESS,
    i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.READY_TO_PAY.TITLE'),
    <PaymentAvailable
      paymentUrl={currentStatus.paymentUrl as string}
      estimate={estimate}
    />,
    '',
    60
  );
};

/**
 * Shows status updated notification.
 */
const showStatusUpdatedNotification = (
  fromStatus: string | null,
  toStatus: string | null
): void => {
  showNotification(
    NotifierType.SUCCESS,
    i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TITLE'),
    <SimpleMessage
      description={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TITLE')}
      message={buildStatusChangeMessage(fromStatus, toStatus)}
    />,
    '',
    0
  );
};

/**
 * Shows appointment pending notification.
 */
const showAppointmentPendingNotification = (
  currentStatus: EstimateStatus
): void => {
  showNotification(
    NotifierType.WARNING,
    i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.TITLE'),
    <StatusError
      description={i18n.t(
        'NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.DESC',
        { ...currentStatus }
      )}
      message={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.MSG', {
        ...currentStatus,
      })}
    />,
    '',
    0
  );
};

/**
 * Shows external error notification for IME messages.
 */
const showExternalErrorNotification = (currentStatus: EstimateStatus): void => {
  showNotification(
    NotifierType.ERROR,
    i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.TITLE'),
    <StatusError
      description={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.DESC', {
        ...currentStatus,
      })}
      message={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.MSG', {
        ...currentStatus,
      })}
    />,
    '',
    0
  );
};

// ============================================================================
// MAIN EXPORTED FUNCTION
// ============================================================================

/**
 * Generates notifications based on a status or status change.
 * Returns status flags that affect save button and polling behavior.
 *
 * @param currentStatus - The current estimate status from the server
 * @param estimate - The fetched estimate data
 * @param onCopyEstimate - Optional callback for "Copy Estimate" action
 * @param onNewEstimate - Optional callback for "New Estimate" action
 * @returns Array of EE_STATUS flags indicating the current state
 */
export const notifyFromStatus = ({
  currentStatus,
  estimate,
  onCopyEstimate,
  onNewEstimate,
}: NotifyFromStatusParams): EE_STATUS[] => {
  // Guard clause for invalid input
  if (!currentStatus.status || !estimate?.estimateNumber) {
    return [EE_STATUS.OPEN];
  }

  // Handle terminal states (closed/expired) - early return
  if (isTerminalStatus(currentStatus.status)) {
    showTerminalStatusNotification(
      currentStatus,
      estimate,
      onCopyEstimate,
      onNewEstimate
    );

    return [EE_STATUS.CLOSED, EE_STATUS.NO_SAVE];
  }

  // At this point, we're in OPEN state
  const returnStatus: EE_STATUS[] = [EE_STATUS.OPEN];

  // Check payment readiness - early return if ready
  if (currentStatus.paymentUrl && isReadyForPayment(currentStatus, estimate)) {
    showPaymentNotification(currentStatus, estimate);

    return [...returnStatus, EE_STATUS.CAN_PAY];
  }

  // Check for status updates (no early return)
  const statusUpdated = currentStatus.status !== estimate.status;

  if (statusUpdated) {
    showStatusUpdatedNotification(estimate.status, currentStatus.status);
  }

  // Check appointment editability
  if (!isAppointmentEditable(currentStatus)) {
    showAppointmentPendingNotification(currentStatus);
    returnStatus.push(EE_STATUS.NO_SAVE);
  }

  // Check for IME error messages
  if (currentStatus.imeMessage) {
    showExternalErrorNotification(currentStatus);
    returnStatus.push(EE_STATUS.ERROR);
  }

  return returnStatus;
};

export const getPaymentUrl = (
  status?: EstimateStatus,
  estimate?: GetEstimateDto
): string | null => status?.paymentUrl ?? estimate?.paymentUrl ?? null;

export const isLatestRevision = (estimate: GetEstimateDto): boolean =>
  // is the given estimate the most recent revision of the chain?
  !!estimate.revisions?.length &&
  estimate.revisions[estimate.revisions.length - 1]?.estimateNumber ===
    estimate?.estimateNumber;

export const isReadyForPayment = (
  status?: EstimateStatus,
  estimate?: GetEstimateDto
): boolean => {
  // must have an estimate number and a payment url, either from the fetched estimate or status update
  if (estimate?.estimateNumber && (estimate.paymentUrl || status?.paymentUrl)) {
    // use the status update if available, otherwise the estimate status rules
    const currentStatus = status?.status ?? estimate?.status;

    return (
      // and current status must be OPEN
      currentStatus === STATUS.OPEN && !!getPaymentUrl(status, estimate)
    );
  }

  return false;
};

/**
 * only certain IME STATUS values can successfully edit/save, all others get an alert
 * and probably restricted save options
 * @param status
 */
export const isAppointmentEditable = (status?: EstimateStatus): boolean =>
  !status?.imeStatus ||
  EDITABLE_IME_STATUS.includes(status?.imeStatus as IME_STATUS);

export const statusChanged = (
  prev: Partial<EstimateStatus>,
  curr: Partial<EstimateStatus>
) => {
  const prevKeys = Object.keys(prev) as Array<keyof EstimateStatus>;
  const currentKeys = Object.keys(curr) as Array<keyof EstimateStatus>;

  if (
    !prevKeys.every((key) => currentKeys.includes(key)) ||
    !currentKeys.every((key) => prevKeys.includes(key))
  ) {
    return true;
  }

  for (const key of prevKeys) {
    if (prev[key] !== curr[key]) {
      return true;
    }
  }

  return false;
};
